import capitalize from '@material-ui/core/utils/capitalize';
import { convertLocale } from '@pe-libs/react-components/build/lib/helper';
import useIntlMessage from '@pe-libs/react-components/build/lib/intl/hooks/useIntlMessage';
import useIntlNumber from '@pe-libs/react-components/build/lib/intl/hooks/useIntlNumber';
import { NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import { getKeyword, getLocation, getQueryParameters } from '#helpers/query';
import { getSearchFinalUrl, getSearchUrl, getUrlWithoutParameters } from '#helpers/searchUrl';
import { globalSystemInfoRequested, setUserIntl } from '#modules/app/actions';
import {
  externalRatingCrawlerCountRequested,
  industriesRequested,
  loadingChanged,
  searchRequested,
} from '#modules/expertDirectory/actions';
import { isExpectedSearchError } from '#modules/expertDirectory/api/search';
import { convertToPhoneLocale } from '#modules/expertDirectory/components/CreateLead/helper';
import Results from '#modules/expertDirectory/components/Results';
import { generateQueryFromUrl, generateUrl } from '#modules/expertDirectory/helpers';
import { getSearchRadius, getSearchResult, getSearchResultData } from '#modules/expertDirectory/selectors';
import { wrapper } from '../../store';
import { StateType } from '../../types';
import messages from '../messages';

type Props = {
  state: StateType;
  host?: string;
  isIndustry: boolean;
  resultsCount: number;
  hasResults: boolean;
  searchContext?: {
    location?: string;
  };
};

const ExpertDirectoryPage: NextPage<Props> = ({ resultsCount, host, isIndustry, searchContext, hasResults }) => {
  const formatMessage = useIntlMessage();
  const formatNumber = useIntlNumber();

  const { query, asPath } = useRouter();
  const keyword = getKeyword(query);
  const location = searchContext?.location || getLocation(query);

  const options = {
    resultsCount: formatNumber(resultsCount, { minimumFractionDigits: 0 }),
    industry: capitalize(generateQueryFromUrl(keyword)),
    location: capitalize(location || 'Deutschland'),
  };

  const seoTitle =
    resultsCount === 0
      ? formatMessage(messages.expertDirectoryIndustryPageNoResultsSeoTitle)
      : formatMessage(messages.expertDirectoryIndustryPageSeoTitle, options);

  const year = new Date().getFullYear();

  const industryDescription = formatMessage(messages.expertDirectoryIndustryCityPageSeoDescription, {
    year,
    resultsCount: formatNumber(resultsCount, { minimumFractionDigits: 0 }),
    industry: isIndustry ? capitalize(generateQueryFromUrl(keyword)) : capitalize(generateQueryFromUrl(keyword)),
    location: capitalize(location || 'Deutschland'),
  });

  const urlWithoutParameters = getUrlWithoutParameters(asPath, host);

  return (
    <>
      <Head>
        <title>{seoTitle}</title>
        <meta name="description" content={industryDescription} />
        <link rel="canonical" href={urlWithoutParameters} />
        <link rel="stylesheet" href="https://unpkg.com/glider-js@1.6.0/glider.min.css" />
        {!hasResults && <meta name="robots" content="noindex" />}
      </Head>
      <Results />
    </>
  );
};

// TODO: refactor getInitialProps to getServerSideProps
// https://nextjs.org/docs/api-reference/data-fetching/getInitialProps
// eslint-disable-next-line complexity
ExpertDirectoryPage.getInitialProps = wrapper.getInitialPageProps((store) => async (context) => {
  const locale = getQueryParameters(context.query, 'lang');
  const defaultLocation = locale === 'de-de' ? 'deutschland' : 'germany';
  const keyword = generateQueryFromUrl(getKeyword(context.query));
  const location = getLocation(context.query) || defaultLocation;
  const page = Number(getQueryParameters(context.query, 'page'));
  const size = 10;
  const isIndustry = Boolean(context.query.slugs?.[0]);
  const searchFinalUrl = getSearchFinalUrl({ locale, query: context.query, asPath: context.asPath ?? '' });
  const searchRadius = getSearchRadius(store.getState());

  if (context.res && locale !== 'de-de') {
    context.res.writeHead(404);
    context.res.end();
  }

  if (context.res && searchFinalUrl !== context.asPath) {
    context.res.writeHead(301, { Location: searchFinalUrl });
    context.res.end();
  }

  if (context.res && context.asPath && !context.asPath.endsWith('/') && !context.asPath.includes('?')) {
    context.res.writeHead(301, { Location: `${context.asPath}/` });
    context.res.end();
  }

  await store.dispatch(globalSystemInfoRequested({ contextRequest: context.req }));
  await store.dispatch(externalRatingCrawlerCountRequested({ contextRequest: context.req }));

  store.dispatch(setUserIntl({ locale: convertToPhoneLocale(locale) }));

  const headers = context.req?.headers as Record<string, string>;

  const searchContext = { location: '' };

  if (keyword && location) {
    try {
      const json = await store
        .dispatch(searchRequested({ keyword, location, page, size, headers, searchRadius }))
        .unwrap();

      searchContext.location = (json.context?.location?.name as string) || '';

      if (!json.context?.industry?.id && context.res && context.query.slugs[0]) {
        context.res.writeHead(404);
        context.res.end();
      }

      if (context.res && json.context?.industry?.id && !isIndustry) {
        const underscoreLocale = convertLocale(locale, 'locale')
          .replace('-', '_')
          .replace('en_GB', 'en_US') as 'de_DE' | 'en_US';
        const url = `/${generateUrl(
          (json.context.industry.name as Record<string, unknown>)[underscoreLocale] as string,
        )}
        /${location}`;

        context.res.writeHead(301, { Location: `/${locale}/${getSearchUrl(locale)}${url}` });
        context.res.end();
      }
    } catch (error) {
      // Expected outcomes (e.g. LOCATION_NOT_FOUND) are valid user-input results, not incidents.
      if (!isExpectedSearchError(error)) {
        /* eslint-disable no-console */
        console.error(error);
      }
    }
  }

  await store.dispatch(industriesRequested());

  store.dispatch(loadingChanged(false));

  const state = store.getState();
  const {
    meta: { count: resultsCount },
  } = getSearchResult(state);

  const hasResults = (getSearchResultData(state) ?? []).length > 0;

  return { host: context.req?.headers.host, isIndustry, searchContext, hasResults, resultsCount };
});

export default ExpertDirectoryPage;
